import { useEffect } from "react";

/**
 * B1-03: Keep the user's scroll position when they leave the browser tab and
 * come back.
 *
 * Some data refreshes (or the browser itself) reset the scroll container to the
 * top when the tab regains visibility/focus. We remember the scroll offset of
 * the given container while the tab is hidden, then restore it once the tab is
 * visible again, but only if something actually moved it.
 *
 * Deliberately in-memory only: nothing persists across a real route change,
 * a reload, or sign-out, so normal navigation still lands at the top.
 */
export function usePreserveScrollOnRefocus(
  getContainer: () => HTMLElement | null,
  /** Changing this (e.g. the route pathname) discards the remembered offset. */
  resetKey?: string,
) {
  useEffect(() => {
    let saved: number | null = null;
    let restoring = false;

    const restore = () => {
      const el = getContainer();
      if (!el || saved === null) return;
      const target = saved;
      restoring = true;
      // Restore across the next few frames: a refetch-driven re-render can
      // land after the visibility event and reset scrollTop again.
      let frames = 0;
      const tick = () => {
        const node = getContainer();
        if (node && Math.abs(node.scrollTop - target) > 2) node.scrollTop = target;
        if (++frames < 12) requestAnimationFrame(tick);
        else restoring = false;
      };
      requestAnimationFrame(tick);
    };

    const remember = () => {
      if (restoring) return;
      const el = getContainer();
      if (el) saved = el.scrollTop;
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") remember();
      else restore();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", remember);
    window.addEventListener("focus", restore);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", remember);
      window.removeEventListener("focus", restore);
    };
  }, [getContainer, resetKey]);
}
